import type {
  AdminRole,
  AdminUser,
  Auth0Client,
  Connection,
  Organization,
  OrganizationMember,
  OrganizationUpsertRequest,
  SetMemberRolesRequest,
} from '@shared/types/admin';
import { api } from './client';

const base = '/api/admin';

// Organizations -------------------------------------------------------------

export function listOrganizations(): Promise<Organization[]> {
  return api.get<Organization[]>(`${base}/organizations`);
}

export function getOrganization(id: string): Promise<Organization> {
  return api.get<Organization>(`${base}/organizations/${encodeURIComponent(id)}`);
}

export function createOrganization(body: OrganizationUpsertRequest): Promise<Organization> {
  return api.post<Organization>(`${base}/organizations`, body);
}

export function updateOrganization(
  id: string,
  body: OrganizationUpsertRequest,
): Promise<Organization> {
  return api.patch<Organization>(`${base}/organizations/${encodeURIComponent(id)}`, body);
}

export function deleteOrganization(id: string): Promise<void> {
  return api.delete(`${base}/organizations/${encodeURIComponent(id)}`);
}

// Organization members ------------------------------------------------------

export function listOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
  return api.get<OrganizationMember[]>(
    `${base}/organizations/${encodeURIComponent(organizationId)}/members`,
  );
}

export function deleteOrganizationMember(organizationId: string, userId: string): Promise<void> {
  return api.delete(
    `${base}/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}`,
  );
}

// Member roles --------------------------------------------------------------

export function listMemberRoles(organizationId: string, userId: string): Promise<AdminRole[]> {
  return api.get<AdminRole[]>(
    `${base}/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/roles`,
  );
}

export function setMemberRoles(
  organizationId: string,
  userId: string,
  body: SetMemberRolesRequest,
): Promise<void> {
  return api.put<void>(
    `${base}/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/roles`,
    body,
  );
}

// Invitations は廃止。招待発行 / 取消は app_accounts 先行の `inviteAccount` / `cancelInvitation`
// (@shared/api/members・accounts) に一本化した。backend の admin invitation エンドポイントは
// それらが内部利用するため残る。

// Users ---------------------------------------------------------------------

export function listUsers(): Promise<AdminUser[]> {
  return api.get<AdminUser[]>(`${base}/users`);
}

export function getUser(userId: string): Promise<AdminUser> {
  return api.get<AdminUser>(`${base}/users/${encodeURIComponent(userId)}`);
}

export function deleteUser(userId: string): Promise<void> {
  return api.delete(`${base}/users/${encodeURIComponent(userId)}`);
}

// Auth0 catalogue (read-only) ----------------------------------------------

export function listRoles(): Promise<AdminRole[]> {
  return api.get<AdminRole[]>(`${base}/roles`);
}

/// org スコープのロールカタログ (招待 / ロール変更ダイアログのロール候補)。super_admin 専用の
/// `listRoles` と別系統で org_admin も自組織分を読める (backend `AdminReadOrgMembers`)。
export function listOrgRoles(organizationId: string): Promise<AdminRole[]> {
  return api.get<AdminRole[]>(
    `${base}/organizations/${encodeURIComponent(organizationId)}/roles`,
  );
}

export function listClients(): Promise<Auth0Client[]> {
  return api.get<Auth0Client[]>(`${base}/clients`);
}

export function listConnections(): Promise<Connection[]> {
  return api.get<Connection[]>(`${base}/connections`);
}
